/**
 * Premium glass-style row for the World Cup leaderboard.
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, Pressable, Platform, useWindowDimensions } from 'react-native';
import { CheckCircle, Lock, MoreVertical, Zap } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { SoccerKit } from '../../ui/SoccerKit';
import { resolveLeaderboardFuturesDisplay } from '../PicksView/futuresCardDisplay.helper';
import { usePlayerRankLookup } from '../../../src/economy/contexts/PlayerRanksContext';
import { PlayerLevelPill } from '../../../src/economy/components/PlayerLevelPill/PlayerLevelPill';
import { LeaderboardUserAvatar } from './LeaderboardUserAvatar';
import { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import { getLeaderboardCardStyles } from './LeaderboardCard.styles';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import {
  LEADERBOARD_RANK_COMPACT_MAX_WIDTH,
  LEADERBOARD_CARD_COPY,
  LEADERBOARD_CARD_A11Y,
  LEADERBOARD_CARD_TEST_IDS,
  LEADERBOARD_LIVE_GAMES_VISIBLE,
  type LeaderboardTeamVisual,
} from './LeaderboardCard.constants';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';

export interface LeaderboardCardProps {
  entry: LeaderboardEntry;
  onPressRow?: (entry: LeaderboardEntry) => void;
  futuresOptions: WorldCupFuturesOptions | null;
  /** When provided, a 3-dot menu button appears in the top-right of the card. */
  onOpenMenu?: (entry: LeaderboardEntry) => void;
  /** Receives the menu-trigger View ref so the parent can measureInWindow(). */
  menuTriggerRef?: (ref: View | null) => void;
  /**
   * Flags (national teams) or kits (clubs). Defaults to `'flag'`, so every existing caller
   * keeps World Cup behaviour by omission (AC-3.6).
   */
  teamVisual?: LeaderboardTeamVisual;
}

const LeaderboardCardComponent: React.FC<LeaderboardCardProps> = ({
  entry,
  onPressRow,
  futuresOptions,
  onOpenMenu,
  menuTriggerRef,
  teamVisual = 'flag',
}) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const compactRank = windowWidth <= LEADERBOARD_RANK_COMPACT_MAX_WIDTH;
  /**
   * In kit mode we hand `CountryFlag` no code at all rather than adding a second render branch:
   * an empty code is exactly the condition its `kitFallback` already exists to cover (AC-3.6).
   */
  const flagCodeFor = (code: string | null | undefined) =>
    teamVisual === 'kit' || !code || code === 'xx' ? '' : code;
  const baseStyles = useMemo(() => getWorldCupLeaderboardStyles(theme), [theme]);
  const styles = useMemo(
    () => getLeaderboardCardStyles(theme, { compactRank }),
    [theme, compactRank],
  );

  const resolved = useMemo(
    () =>
      resolveLeaderboardFuturesDisplay(
        entry.winnerTeamId,
        entry.topScorerPlayerId,
        futuresOptions,
      ),
    [entry.winnerTeamId, entry.topScorerPlayerId, futuresOptions],
  );
  const winnerCode = resolved.winnerCountryCode ?? undefined;
  const winnerLabel = resolved.winnerCaption;
  /**
   * Only in kit mode. Supplying it in flag mode would change what the World Cup card draws when
   * a flag URL 404s (a blank plate today), and that is not this change's business.
   */
  const winnerKitFallback = useMemo(
    () => (teamVisual === 'kit' ? getTeamKitConfig(winnerLabel ?? '') : undefined),
    [teamVisual, winnerLabel],
  );
  const scorerShort = resolved.scorerCaption;
  const hits = entry.exactHitCount ?? 0;
  // Null unless a PlayerRanksProvider is mounted above (global WC leaderboard only).
  const playerRank = usePlayerRankLookup()(entry.userId);
  const hasLiveReveal = Array.isArray(entry.livePredictions);
  const livePredictions = entry.livePredictions ?? [];
  // Capped for DISPLAY only (specs/champions-league AC-18.1/AC-18.2). The server keeps sending
  // every live prediction, because `sumLivePoints` runs over the full array and the squad board
  // re-ranks on it — truncating the payload would silently change both the total and the order.
  // So the overflow count is derived here, from what the server actually sent.
  const visibleLivePredictions = livePredictions.slice(0, LEADERBOARD_LIVE_GAMES_VISIBLE);
  const hiddenLiveCount = livePredictions.length - visibleLivePredictions.length;
  // Provisional live points already folded into `entry.points` (squad re-rank).
  const livePoints = entry.livePoints ?? 0;

  const handlePress = useCallback(() => {
    onPressRow?.(entry);
  }, [entry, onPressRow]);

  const handleOpenMenu = useCallback(() => {
    onOpenMenu?.(entry);
  }, [entry, onOpenMenu]);

  const cardOuterStyle = [
    styles.cardOuter,
    entry.rank === 1 && styles.cardOuterRank1,
    entry.rank === 2 && styles.cardOuterRank2,
    entry.rank === 3 && styles.cardOuterRank3,
    entry.isCurrentUser && styles.cardOuterMe,
  ];

  return (
    <Pressable
      onPress={handlePress}
      style={({ pressed }) => [
        styles.pressRoot,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.pressedOpacity,
      ]}
    >
      {({ hovered }) => (
        <View style={[cardOuterStyle, hovered && Platform.OS === 'web' && styles.hoverWeb]}>
          {onOpenMenu ? (
            <View
              ref={menuTriggerRef}
              collapsable={false}
              style={styles.menuTriggerWrap}
            >
              <Pressable
                onPress={handleOpenMenu}
                style={styles.menuTriggerButton}
                accessibilityLabel={LEADERBOARD_CARD_A11Y.optionsForUser(entry.name)}
                accessibilityRole="button"
                hitSlop={8}
              >
                <MoreVertical size={18} color={theme.gray[600]} />
              </Pressable>
            </View>
          ) : null}
          <View style={styles.row}>
            <View style={[styles.rankCol, styles.colAlignCenter]}>
              <Text style={styles.rankNumberText}>{entry.rank}</Text>
            </View>

            <View style={styles.avatarCol}>
              <LeaderboardUserAvatar
                entry={entry}
                variant="row"
                styles={baseStyles}
              />
            </View>

            <View style={styles.nameCol}>
              <View style={styles.nameRow}>
                <Text
                  style={[styles.userName, entry.isCurrentUser && styles.userNameMe]}
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {entry.name}
                </Text>
                {hits > 0 ? (
                  <View style={styles.hitsBadge}>
                    <CheckCircle size={9} color={theme.background.primary} strokeWidth={3} />
                    <Text style={styles.hitsBadgeText}>{hits}</Text>
                  </View>
                ) : null}
              </View>
              {playerRank != null ? (
                <PlayerLevelPill level={playerRank.level} style={styles.rankLevelPill} />
              ) : null}
            </View>

            <View style={styles.pickPair}>
              {entry.futuresHidden ? (
                <>
                  <View
                    style={styles.pickTileCol}
                    accessibilityLabel={LEADERBOARD_CARD_COPY.LOCKED_A11Y}
                  >
                    <View style={styles.flagTile}>
                      <Lock size={15} color={theme.gray[500]} />
                    </View>
                    <Text style={styles.pickCaption} numberOfLines={1}>
                      {LEADERBOARD_CARD_COPY.LOCKED_PLACEHOLDER}
                    </Text>
                  </View>
                  <View style={styles.pickTileCol}>
                    <View style={styles.kitTile}>
                      <Lock size={15} color={theme.gray[500]} />
                    </View>
                    <Text style={styles.pickCaption} numberOfLines={1}>
                      {LEADERBOARD_CARD_COPY.LOCKED_PLACEHOLDER}
                    </Text>
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.pickTileCol}>
                    <View style={styles.flagTile}>
                      <CountryFlag
                        countryCode={flagCodeFor(winnerCode)}
                        kitFallback={winnerKitFallback}
                        size="compact"
                        accessibilityLabel={winnerLabel ?? 'Winner not picked'}
                      />
                    </View>
                    <Text style={styles.pickCaption} numberOfLines={1}>
                      {winnerLabel ?? LEADERBOARD_CARD_COPY.EMPTY_PLACEHOLDER}
                    </Text>
                  </View>
                  <View style={styles.pickTileCol}>
                    <View style={styles.kitTile}>
                      <SoccerKit
                        size={28}
                        primary={resolved.scorerKitMeta?.kitColor ?? theme.gray[400]}
                        secondary={resolved.scorerKitMeta?.trimColor ?? theme.gray[500]}
                        number={resolved.scorerKitMeta?.number}
                      />
                    </View>
                    <Text style={styles.pickCaption} numberOfLines={1}>
                      {scorerShort || LEADERBOARD_CARD_COPY.EMPTY_PLACEHOLDER}
                    </Text>
                  </View>
                </>
              )}
            </View>

            <View style={styles.ptsWrap}>
              <View style={styles.ptsCol}>
                <Text style={styles.pointsValue}>{entry.points}</Text>
                <Text style={styles.pointsLabel}>pts</Text>
              </View>
              {livePoints > 0 ? (
                <Text style={styles.livePointsDelta}>{`+${livePoints} live`}</Text>
              ) : null}
            </View>
          </View>
          {hasLiveReveal ? (
            <View style={styles.liveStrip}>
              <View style={styles.liveBadge}>
                <Text style={styles.liveBadgeText}>{LEADERBOARD_CARD_COPY.LIVE_BADGE}</Text>
              </View>
              {livePredictions.length > 0 ? (
                <View
                  style={styles.liveGamesInline}
                  testID={LEADERBOARD_CARD_TEST_IDS.liveStrip(entry.userId)}
                >
                  {visibleLivePredictions.map((live) => {
                    const chipStyle =
                      live.outcome === 'hit'
                        ? styles.liveGameChipHit
                        : live.outcome === 'result'
                          ? styles.liveGameChipResult
                          : styles.liveGameChipMiss;
                    const scoreTextStyle =
                      live.outcome === 'hit'
                        ? styles.liveScoreTextHit
                        : live.outcome === 'result'
                          ? styles.liveScoreTextResult
                          : styles.liveScoreTextMiss;
                    return (
                      <View
                        key={live.matchId}
                        style={styles.liveGameItem}
                        accessibilityLabel={LEADERBOARD_CARD_A11Y.livePrediction({
                          homeTeam: live.homeTeam,
                          awayTeam: live.awayTeam,
                          predictionHome: live.prediction.home,
                          predictionAway: live.prediction.away,
                          outcome: live.outcome,
                          pointsEarned: live.pointsEarned,
                          isDoublePoints: live.isDoublePoints,
                        })}
                      >
                        <View style={[styles.liveGameChip, chipStyle]}>
                          <CountryFlag
                            countryCode={flagCodeFor(live.homeCountryCode)}
                            kitFallback={getTeamKitConfig(live.homeTeam)}
                            size="mini"
                            accessibilityLabel={live.homeTeam}
                          />
                          <Text style={[styles.liveScoreText, scoreTextStyle]}>
                            {`${live.prediction.home}-${live.prediction.away}`}
                          </Text>
                          <CountryFlag
                            countryCode={flagCodeFor(live.awayCountryCode)}
                            kitFallback={getTeamKitConfig(live.awayTeam)}
                            size="mini"
                            accessibilityLabel={live.awayTeam}
                          />
                          <View
                            style={[
                              styles.liveBoostSlot,
                              live.isDoublePoints && styles.liveBoostGlyph,
                            ]}
                            pointerEvents="none"
                          >
                            <Zap
                              size={12}
                              color={live.isDoublePoints ? theme.accent : theme.gray[400]}
                              fill={live.isDoublePoints ? theme.accent : 'transparent'}
                              strokeWidth={2.4}
                            />
                          </View>
                        </View>
                        <Text style={styles.livePointsText}>
                          {`+${live.pointsEarned}`}
                          {live.judgedOnRegularTime ? (
                            <Text style={styles.live90MinCue}>{" 90'"}</Text>
                          ) : null}
                        </Text>
                      </View>
                    );
                  })}
                  {hiddenLiveCount > 0 ? (
                    <Text
                      style={styles.liveMoreGames}
                      testID={LEADERBOARD_CARD_TEST_IDS.liveMore(entry.userId)}
                    >
                      {LEADERBOARD_CARD_COPY.liveMoreGames(hiddenLiveCount)}
                    </Text>
                  ) : null}
                </View>
              ) : (
                <Text style={styles.liveStripNoPick}>-</Text>
              )}
            </View>
          ) : null}
        </View>
      )}
    </Pressable>
  );
};

/**
 * Memoized: rows mount inside a recycling FlashList whose parent re-renders on
 * scroll (self-row visibility state). The `entry` objects are referentially
 * stable between those re-renders, so shallow-prop memoization skips a full
 * re-render — and the `<Text>` StaticLayout re-measure it triggers — on every
 * unrelated parent update. Implicated in an Android-16 GC-stall ANR sampled
 * mid-text-measure during a Fabric mount batch.
 */
export const LeaderboardCard = React.memo(LeaderboardCardComponent);
